import { inject, Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';

import { AuthEvent } from '@ninety/mobile/platform/auth';

import { classifyFailure } from './auth-failure';
import { AuthorizationServer, GrantStep } from './authorization-server.service';
import { CommandRunner } from './command-runner.service';
import { PendingCredentials } from './pending-credentials.service';

/**
 * The Commands that turn a password into a Cognito session.
 *
 * Both sign-in Commands answer with the same two possibilities — an ID Token, or a Challenge — so
 * both end in {@link eventFor}. Neither produces a session by itself: an ID Token is not
 * a session until the Resource Server has exchanged it, which is `SessionTokensEffects`' half.
 *
 * ## What a failure here is, and is not
 *
 * A rejected Grant is `grantFailed`, never `refreshFailed`: `transition()` responds to the two
 * completely differently, and it cannot tell them apart from an exception. A failed Grant never
 * clears the Vault — someone mistyping a password on a device holding a good Durable Root must
 * keep it.
 *
 * The password is a parameter and nothing else. It is not logged, not stored, and not put on an
 * event.
 *
 * The one thing that outlives the call is that a successful password sign-in hands the pair to
 * {@link PendingCredentials}, in memory, for the enrollment offer that may follow. **Nothing is written
 * to the device here** — that happens once somebody accepts, see `SessionBiometricsEffects`.
 */
@Injectable()
export class SessionGrantEffects {
  private readonly commands = inject(CommandRunner);
  private readonly authorizationServer = inject(AuthorizationServer);
  private readonly pending = inject(PendingCredentials);

  /**
   * The password is held only once the Authorization Server has accepted it, so a mistyped one is never
   * carried anywhere. A Challenge counts as accepted — the password was right, and the offer comes
   * after the code.
   */
  readonly srpSignIn$ = createEffect(() =>
    this.commands.on('srpSignIn', async (command): Promise<AuthEvent> => {
      try {
        const step = eventFor(await this.authorizationServer.signInWithPassword(command.email, command.password));
        this.pending.hold(command.email, command.password);
        return step;
      } catch (error) {
        return { type: 'grantFailed', cause: classifyFailure(error) };
      }
    })
  );

  readonly confirmChallenge$ = createEffect(() =>
    this.commands.on('confirmChallenge', async (command): Promise<AuthEvent> => {
      try {
        return eventFor(await this.authorizationServer.confirmChallenge(command.code));
      } catch (error) {
        return { type: 'grantFailed', cause: classifyFailure(error) };
      }
    })
  );

  /**
   * Teardown, so there is no outcome for the machine to decide anything about.
   *
   * A failure is still worth knowing about, because this is what stops the app rehydrating a Cognito
   * session the Resource Server has already refused — the seam reports it as `auth.command_failed`
   * and the state stays where the machine put it.
   */
  readonly cognitoSignOut$ = createEffect(() =>
    this.commands.teardown('cognitoSignOut', () => this.authorizationServer.signOut())
  );
}

/** Which event a Grant step means. Shared, because a Challenge can arrive at either step. */
function eventFor(step: GrantStep): AuthEvent {
  return step.kind === 'challenge'
    ? { type: 'challengeIssued', challenge: step.challenge }
    : { type: 'idTokenObtained', idToken: step.idToken };
}
